import React from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    PointElement
} from "chart.js";
import { Chart } from "react-chartjs-2";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import annotationPlugin from 'chartjs-plugin-annotation';

// Register the thresholdBackgroundPlugin locally
const thresholdBackgroundPlugin = {
    id: 'thresholdBackground',
    beforeDraw: (chart) => {
        const { ctx, chartArea, scales } = chart;
        if (!chartArea) return;
        
        const { top, bottom, left, right, width } = chartArea;
        const { y } = scales;
        
        if (!y) return;
        
        // Get thresholds from the chart's options
        const thresholds = chart.options.plugins.thresholds || {};
        console.log('Threshold plugin values:', thresholds);
        
        if (!thresholds.low && !thresholds.normalLow && !thresholds.normalHigh && !thresholds.high) {
            console.warn('Missing threshold values', thresholds);
            return;
        }

        
        // We draw from bottom to top
        // Very Low Zone (Red) - from 0 to low
        const lowY = y.getPixelForValue(thresholds.low);
        ctx.fillStyle = 'rgba(255, 138, 138, 0.3)'; // Red with 30% opacity
        ctx.fillRect(left, lowY, width, bottom - lowY);
        
        // Low Zone (Yellow) - from low to normalLow
        const normalLowY = y.getPixelForValue(thresholds.normalLow);
        ctx.fillStyle = 'rgba(255, 229, 102, 0.4)'; // Yellow with 30% opacity
        ctx.fillRect(left, normalLowY, width, lowY - normalLowY);
        
        // Normal Zone (Green) - from normalLow to normalHigh
        const normalHighY = y.getPixelForValue(thresholds.normalHigh);
        ctx.fillStyle = 'rgba(144, 255, 140, 0.8)'; // Green with 80% opacity
        ctx.fillRect(left, normalHighY, width, normalLowY - normalHighY);
        
        // High Zone (Yellow) - from normalHigh to high
        const highY = y.getPixelForValue(thresholds.high);
        ctx.fillStyle = 'rgba(255, 229, 102, 0.4)'; // Yellow with 30% opacity
        ctx.fillRect(left, highY, width, normalHighY - highY);
        
        // Very High Zone (Red) - from high to max
        ctx.fillStyle = 'rgba(255, 138, 138, 0.3)'; // Red with 30% opacity
        ctx.fillRect(left, top, width, highY - top);
        
        // Fill with white background
        ctx.fillStyle = 'white';
        ctx.fillRect(left, top, width, bottom - top);
    }
};

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend,
    ChartDataLabels,
    annotationPlugin
);

// Updated getBarColor function to handle zero values
const getBarColor = (value, thresholds, zeroValue = false) => {
    // If it's a zero value, we still want to show the threshold color it would have
    if (zeroValue) {
        return 'rgba(144, 255, 140, 0.5)'; // light green for zero values
    }
    
    if (value <= thresholds.low) return '#FF8A8A'; // Red (very low)
    if (value <= thresholds.normalLow) return '#FFE566'; // Yellow (low)
    if (value <= thresholds.normalHigh) return '#90FF8C'; // Green (normal)
    if (value <= thresholds.high) return '#FFE566'; // Yellow (high)
    return '#FF8A8A'; // Red (very high)
};

const ModularTrendsChart = ({ title, metricLabel, historyData, thresholds, minValue, maxValue }) => {
    // Format date for x-axis labels
    const formatDateLabel = (dateString) => {
        if (!dateString) return '';
        
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                console.warn('Invalid date:', dateString);
                return '';
            }
            
            const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
            const monthDay = date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric'
            });
            
            return `${weekday}\n${monthDay}`;
        } catch (error) {
            console.error('Date parsing error:', error);
            return '';
        }
    };

    // Extract values and dates from historyData
    const processedData = React.useMemo(() => {
        if (!Array.isArray(historyData)) return { values: [], dates: [] };

        // Process data and then reverse both arrays to show oldest to newest
        const processed = historyData.reduce((acc, item) => {
            let value = typeof item === 'object' ? parseFloat(item.value) : parseFloat(item);
            let date = typeof item === 'object' ? item.period_start : null;
            
            // Handle time format like "8h 30m" for sleep data
            if (typeof item === 'object' && item.value && typeof item.value === 'string') {
                const timeMatch = item.value.match(/^(\d{1,2})h\s(\d{1,2})m$/);
                if (timeMatch) {
                    // Convert hours and minutes to total minutes for the chart
                    const hours = parseInt(timeMatch[1], 10);
                    const minutes = parseInt(timeMatch[2], 10);
                    value = hours * 60 + minutes; // Convert to total minutes
                }
            }
            
            acc.values.push(isNaN(value) ? 0 : value);
            acc.dates.push(date ? formatDateLabel(date) : '');

            return acc;
        }, { values: [], dates: [] });

        // Reverse both arrays to show oldest to newest (left to right)
        return {
            values: processed.values.reverse(),
            dates: processed.dates.reverse()
        };
    }, [historyData]);

    // Determine if a value is zero for coloring purposes
    const zeroValues = processedData.values.map(value => value === 0);
    
    // Pass zeroValues to getBarColor function
    const barColors = processedData.values.map((value, index) => 
        getBarColor(value, thresholds, zeroValues[index])
    );

    // Calculate full range for thresholds to ensure all zones are visible
    const getFullThresholdRange = () => {
        const stdDev = thresholds.high - thresholds.normalHigh;
        
        // Find actual min and max values in the data
        const maxDataValue = Math.max(...processedData.values);
        const minDataValue = Math.min(...processedData.values);
        
        // Calculate threshold-based min and max
        let maxThreshold = thresholds.high + stdDev;
        let minThreshold = Math.max(0, thresholds.low - stdDev); // Subtract stdDev from low, but keep >= 0
        
        // Adjust if data exceeds current thresholds (add buffer of 2-3 units)
        if (maxDataValue > maxThreshold) {
            maxThreshold = maxDataValue + 1;
        }
        
        if (minDataValue < minThreshold) {
            minThreshold = Math.max(0, minDataValue - 2); // Keep min at least 0
        }
        
        // For sleep data, cap at 24 hours (1440 minutes)
        if (metricLabel.includes('Sleep')) {
            return {
                min: minThreshold,
                max: Math.min(maxThreshold, 1440)
            };
        }
        
        return {
            min: minThreshold,
            max: maxThreshold
        };
    };

    const thresholdRange = getFullThresholdRange();
    
    // Use threshold range directly for chart min/max
    const effectiveMinValue = thresholdRange.min;
    const effectiveMaxValue = thresholdRange.max;

    // Calculate gradient stops based on full range
    const getPercentage = (value) => {
        const numValue = parseFloat(value);
        if (isNaN(numValue)) return 0;
        const range = effectiveMaxValue - effectiveMinValue;
        // Invert the percentage calculation to match chart orientation
        return Math.min(Math.max((100 - ((numValue - effectiveMinValue) / range * 100)), 0), 100).toFixed(1);
    };

    // Ensure all threshold values are numbers
    const validThresholds = {
        low: Math.max(0, parseFloat(thresholds.low) || 0),
        normalLow: Math.max(0, parseFloat(thresholds.normalLow) || 0),
        average: Math.max(0, parseFloat(thresholds.average) || 0),
        normalHigh: Math.max(0, parseFloat(thresholds.normalHigh) || effectiveMaxValue),
        high: Math.max(0, parseFloat(thresholds.high) || effectiveMaxValue),
        status: thresholds.status || 'Unknown'
    };

    const calculateDailyStatuses = (historyData, validThresholds) => {
        // Check if we have valid data to process
        if (!historyData || !Array.isArray(historyData) || historyData.length === 0) {
            return ['No Data'];
        }

        const statusList = historyData.map(data => {
            // Handle null/undefined values or missing value property
            if (!data || data.value === null || data.value === undefined) {
                return 'No Data';
            }

            // Parse the value if it's a sleep time format (e.g., "8h 30m")
            let value = data.value;
            if (typeof value === 'string' && value.match(/^\d{1,2}h\s\d{1,2}m$/)) {
                value = parseTimeToMinutes(value);
            }

            // Evaluate against thresholds to determine status
            if (value <= validThresholds.low) return 'Caution';
            if (value <= validThresholds.normalLow) return 'Attention';
            if (value <= validThresholds.normalHigh) return 'Good';
            if (value <= validThresholds.high) return 'Attention';
            return 'Caution';
        });
        return statusList;
    }
    
    const statusList = calculateDailyStatuses(historyData, validThresholds);
    
    const calculateWeeklyStatus = (statusList) => {
        // If we have no status list or it's empty, return 'No Data'
        if (!statusList || statusList.length === 0) {
            return 'No Data';
        }
        
        // If there is not enough data, return 'Insufficient data'
        const noDataCount = statusList.filter(status => status === 'No Data').length;
        if (noDataCount >= 3 || statusList.length < 3) return 'Insufficient data';

        // Calculate weekly status based on the most common status of the week
        const weeklyStatusCounts = statusList.reduce((acc, status) => {
            acc[status] = (acc[status] || 0) + 1;
            return acc;
        }, {});
        
        // If all counts are equal, prioritize in this order: Good > Attention > Caution
        const statusPriority = { 'Good': 3, 'Attention': 2, 'Caution': 1, 'No Data': 0 };
        
        const mostCommonStatus = Object.keys(weeklyStatusCounts).reduce((a, b) => {
            // If counts are the same, choose the one with higher priority
            if (weeklyStatusCounts[a] === weeklyStatusCounts[b]) {
                return statusPriority[a] > statusPriority[b] ? a : b;
            }
            // Otherwise choose the one with higher count
            return weeklyStatusCounts[a] > weeklyStatusCounts[b] ? a : b;
        });
        
        return mostCommonStatus;
    };
    
    const weeklyStatus = calculateWeeklyStatus(statusList);

    // Gradient goes from bottom to top to match chart orientation
    const gradientBackground = `linear-gradient(to top, 
        #FF8A8A 0% ${100 - getPercentage(validThresholds.low)}%, 
        #FFE566 ${100 - getPercentage(validThresholds.low)}% ${100 - getPercentage(validThresholds.normalLow)}%, 
        #90FF8C ${100 - getPercentage(validThresholds.normalLow)}% ${100 - getPercentage(validThresholds.normalHigh)}%, 
        #FFE566 ${100 - getPercentage(validThresholds.normalHigh)}% ${100 - getPercentage(validThresholds.high)}%, 
        #FF8A8A ${100 - getPercentage(validThresholds.high)}% 100%)`;

    // Format minutes into hours and minutes or add RR unit for respiratory rate
    const formatDuration = (minutes) => {
        if (metricLabel.includes('Respiratory Rate')) {
            const value = typeof minutes === 'number' ? minutes.toFixed(1) : '0.0';
            return `${value} bpm`;
        }
        if (metricLabel.includes('Sleep')) {
            // Cap sleep duration at 24 hours (1440 minutes)
            const cappedMinutes = Math.min(minutes, 1440);
            const hours = Math.floor(cappedMinutes / 60);
            const mins = Math.round(cappedMinutes % 60);
            return `${hours}h ${mins}m`;
        }
        return typeof minutes === 'number' ? minutes.toFixed(1) : '0.0';
    };

    const formatLabel = (value) => {
        if (typeof value === 'object' && value.value && value.unit) {
            return (
                <div style={{ display: 'inline-flex', alignItems: 'flex-start' }}>
                    <span>{value.value}</span>
                    <span style={{ fontSize: '0.7em', marginLeft: '2px', marginTop: '-2px' }}>{value.unit}</span>
                </div>
            );
        }
        return value;
    };

    const data = {
        labels: processedData.dates,
        datasets: [
            {
                type: "line",
                label: "Trend",
                data: processedData.values,
                borderColor: "#3498db",
                borderWidth: 4,
                tension: 0.4,
                fill: false,
                pointBackgroundColor: "#3498db",
                pointRadius: 4,
                order: 1,
                datalabels: {
                    display: false
                },
                // Custom legend styling for line
                pointStyle: 'line',
                pointBorderWidth: 2
            },
            {
                label: metricLabel,
                data: processedData.values,
                type: "bar",
                backgroundColor: 'rgba(200, 200, 200, 0.7)',  // Restore the threshold-based colors
                borderColor: "#4B4B4D",      // Light grey
                borderWidth: 1,
                order: 2,
                // Legend styling only (won't affect the actual bars)
                legendPointStyle: 'rect',
                legendBorderColor: 'black',
                legendBackgroundColor: 'transparent'
            }
        ]
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
            padding: {
                left: 0,
                top: 30,
                right: 20,
                bottom: 10
            }
        },
        scales: {
            y: {
                min: effectiveMinValue,
                max: effectiveMaxValue,
                grid: {
                    display: true
                },
                title: {
                    display: true,
                    text: metricLabel.includes('Respiratory Rate') ? 'Respiratory Rate (bpm)' :
                          metricLabel.includes('Sleep') ? 'Total Sleep (hours&mins)' :
                          metricLabel.includes('Bed Exit') ? 'Bed Exits' : '',
                    font: {
                        size: 14,
                        weight: 'bold'
                    }
                },
                ticks: {
                    callback: function(value) {
                        // Add threshold labels with proper formatting
                        const thresholdValue = [
                            validThresholds.low,
                            validThresholds.normalLow,
                            validThresholds.normalHigh,
                            validThresholds.high
                        ];
                        
                        // For threshold values, add a small indicator
                        if (thresholdValue.includes(value)) {
                            return `→ ${formatDuration(Math.max(0, value))}`;
                        }
                        
                        return formatDuration(Math.max(0, value));
                    },
                    // Add key threshold values to ensure they appear on the axis
                    includeBounds: true,
                    // Force include the threshold values
                    afterBuildTicks: function(scale) {
                        const originalTicks = scale.ticks;
                        
                        // Add our threshold values
                        const thresholdsTicks = [
                            { value: 0 }, // Always show zero
                            { value: validThresholds.low },
                            { value: validThresholds.normalLow },
                            { value: validThresholds.normalHigh },
                            { value: validThresholds.high }
                        ];
                        
                        // Combine with existing ticks, filter duplicates and sort
                        const combinedTicks = [...originalTicks, ...thresholdsTicks];
                        const uniqueTicks = Array.from(new Map(combinedTicks.map(tick => 
                            [tick.value, tick])).values());
                        
                        // Sort by value
                        uniqueTicks.sort((a, b) => a.value - b.value);
                        
                        return uniqueTicks;
                    }
                },
                beginAtZero: true
            },
            x: {
                grid: {
                    display: false
                },
                title: {
                    display: true,
                    text: 'Daily Reports',
                    font: {
                        size: 14,
                        weight: 'bold'
                    }
                },
                ticks: {
                    maxRotation: 0,
                    minRotation: 0,
                    font: {
                        size: 11
                    },
                    padding: 10
                }
            }
        },
        plugins: {
            datalabels: {
                display: true,
                color: 'black',
                anchor: 'end',
                align: 'top',
                offset: -2,
                formatter: function(value) {
                    return formatDuration(Math.max(0, value));
                },
                font: {
                    weight: 'bold',
                    size: 11
                }
            },
            legend: {
                display: true,
                position: 'top',
                align: 'center',
                onHover: (event, legendItem, legend) => {
                    const tooltips = {
                        'Bed Exits': 'Bed Exits counted',
                        'Secondary Presence': 'Secondary Presence counted',
                        'Trend': 'Trend of the metric'
                    };
                    
                    // Change cursor to indicate interactivity
                    event.native.target.style.cursor = 'pointer';
                    
                    // Find tooltip container or create it if it doesn't exist
                    let tooltipEl = document.getElementById('chart-legend-tooltip');
                    if (!tooltipEl) {
                        tooltipEl = document.createElement('div');
                        tooltipEl.id = 'chart-legend-tooltip';
                        tooltipEl.style.position = 'absolute';
                        tooltipEl.style.background = 'rgba(0,0,0,0.7)';
                        tooltipEl.style.color = 'white';
                        tooltipEl.style.padding = '5px 10px';
                        tooltipEl.style.borderRadius = '3px';
                        tooltipEl.style.fontSize = '12px';
                        tooltipEl.style.pointerEvents = 'none';
                        tooltipEl.style.opacity = '0';
                        tooltipEl.style.transition = 'opacity 0.2s';
                        tooltipEl.style.zIndex = '10';
                        document.body.appendChild(tooltipEl);
                    }
                    
                    // Debug: Log the exact legend item text to see what we're working with
                    console.log('Legend item text:', JSON.stringify(legendItem.text));
                    console.log('Available tooltip keys:', Object.keys(tooltips));
                    
                    // Show tooltip with description
                    if (tooltips[legendItem.text]) {
                        tooltipEl.innerHTML = tooltips[legendItem.text];
                        tooltipEl.style.opacity = '1';
                        tooltipEl.style.left = event.native.pageX + 'px';
                        tooltipEl.style.top = (event.native.pageY - 28) + 'px'; // Position above the cursor
                    } else {
                        // Fallback: Try to find a close match for the text
                        const keys = Object.keys(tooltips);
                        const closeMatch = keys.find(key => 
                            key.toLowerCase().includes('night') && 
                            key.toLowerCase().includes('sleep'));
                        
                        if (closeMatch) {
                            tooltipEl.innerHTML = tooltips[closeMatch];
                            tooltipEl.style.opacity = '1';
                            tooltipEl.style.left = event.native.pageX + 'px';
                            tooltipEl.style.top = (event.native.pageY - 28) + 'px';
                            
                            // Log that we're using a close match
                            console.log('Using close match:', closeMatch, 'for', legendItem.text);
                        }
                    }
                },
                onLeave: (event, legendItem, legend) => {
                    // Reset cursor
                    if (event.native && event.native.target) {
                        event.native.target.style.cursor = 'default';
                    }
                    
                    // Hide tooltip
                    const tooltipEl = document.getElementById('chart-legend-tooltip');
                    if (tooltipEl) {
                        tooltipEl.style.opacity = '0';
                    }
                },
                labels: {
                    boxWidth: 30,
                    padding: 15,
                    usePointStyle: true,
                    pointStyle: true,
                    font: {
                        size: 12,
                        weight: '500'
                    },
                    color: '#444',
                    generateLabels: function(chart) {
                        const datasets = chart.data.datasets;
                        return datasets.map((dataset, i) => ({
                            text: dataset.label,
                            fillStyle: i === 0 ? dataset.borderColor : 'transparent',
                            strokeStyle: i === 0 ? dataset.borderColor : 'black',
                            lineWidth: dataset.borderWidth,
                            hidden: !chart.isDatasetVisible(i),
                            index: i,
                            // Use custom legend style for bars, but default for line
                            pointStyle: i === 0 ? dataset.pointStyle : dataset.legendPointStyle,
                            // Use custom colors for legend only
                            borderColor: i === 0 ? dataset.borderColor : dataset.legendBorderColor,
                            backgroundColor: i === 0 ? dataset.borderColor : dataset.legendBackgroundColor
                        }));
                    }
                }
            },
            title: {
                display: true,
                text: [title],
                font: {
                    size: 16,
                    weight: 'bold'
                }
            },
            annotation: {
                annotations: {
                    // lowLine: {
                    //     type: 'line',
                    //     yMin: validThresholds.low,
                    //     yMax: validThresholds.low,
                    //     borderColor: '#FF8A8A', // Red
                    //     borderWidth: 2,
                    //     borderDash: [5, 5],
                    //     drawTime: 'beforeDatasetsDraw', // Move behind everything
                    //     label: {
                    //         display: false
                    //     }
                    // },
                    // normalLowLine: {
                    //     type: 'line',
                    //     yMin: validThresholds.normalLow,
                    //     yMax: validThresholds.normalLow,
                    //     borderColor: '#FFE566', // Yellow
                    //     borderWidth: 2,
                    //     borderDash: [5, 5],
                    //     drawTime: 'beforeDatasetsDraw', // Move behind everything
                    //     label: {
                    //         display: false
                    //     }
                    // },
                    // normalHighLine: {
                    //     type: 'line',
                    //     yMin: validThresholds.normalHigh,
                    //     yMax: validThresholds.normalHigh,
                    //     borderColor: '#FFE566', // Yellow
                    //     borderWidth: 2,
                    //     borderDash: [5, 5],
                    //     drawTime: 'beforeDatasetsDraw', // Move behind everything
                    //     label: {
                    //         display: false
                    //     }
                    // },
                    // highLine: {
                    //     type: 'line',
                    //     yMin: validThresholds.high,
                    //     yMax: validThresholds.high,
                    //     borderColor: '#FF8A8A', // Red
                    //     borderWidth: 2,
                    //     borderDash: [5, 5],
                    //     drawTime: 'beforeDatasetsDraw', // Move behind everything
                    //     label: {
                    //         display: false
                    //     }
                    // }
                }
            },
        }
    };

    return (
        <div style={{ position: 'relative', height: '600px', width: '100%', marginTop: '30px', marginBottom: '30px'  }}>
            <div style={{ height: '100%', width: '100%' }}>
            <Chart 
                data={data} 
                options={options} 
                plugins={[
                    {
                        ...thresholdBackgroundPlugin,
                        // Override beforeDraw to directly use validThresholds
                        beforeDraw: (chart) => {
                            const { ctx, chartArea, scales } = chart;
                            if (!chartArea) return;
                            
                            const { top, bottom, left, right, width } = chartArea;
                            const { y } = scales;
                            
                            if (!y) return;
                            
                            // Directly use validThresholds instead of trying to get from chart options
                            const thresholds = validThresholds;
                            
                            // Very Low Zone (Red) - from 0 to low
                            const lowY = y.getPixelForValue(thresholds.low);
                            ctx.fillStyle = 'rgba(255, 138, 138, 0.3)'; // Red with 30% opacity
                            ctx.fillRect(left, lowY, width, bottom - lowY);
                            
                            // Low Zone (Yellow) - from low to normalLow
                            const normalLowY = y.getPixelForValue(thresholds.normalLow);
                            ctx.fillStyle = 'rgba(255, 229, 102, 0.3)'; // Yellow with 30% opacity
                            ctx.fillRect(left, normalLowY, width, lowY - normalLowY);
                            
                            // Normal Zone (Green) - from normalLow to normalHigh
                            const normalHighY = y.getPixelForValue(thresholds.normalHigh);
                            ctx.fillStyle = 'rgba(144, 255, 140, 0.8)'; // Green with 80% opacity
                            ctx.fillRect(left, normalHighY, width, normalLowY - normalHighY);
                            
                            // High Zone (Yellow) - from normalHigh to high
                            const highY = y.getPixelForValue(thresholds.high);
                            ctx.fillStyle = 'rgba(255, 229, 102, 0.3)'; // Yellow with 30% opacity
                            ctx.fillRect(left, highY, width, normalHighY - highY);
                            
                            // Very High Zone (Red) - from high to max
                            ctx.fillStyle = 'rgba(255, 138, 138, 0.3)'; // Red with 30% opacity
                            ctx.fillRect(left, top, width, highY - top);
                            
                            // Fill with white background
                            ctx.fillStyle = 'white';
                            ctx.fillRect(left, top, width, bottom - top);
                        }
                    }
                ]}
            />
            </div>
        </div>
    );
};

export default ModularTrendsChart;
